import { Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { useContext, useEffect, useState } from "react";
import SpecialistApi from "../../../api/specialistsApi";
import Loader from "../../../components/Loader/Loader";
import ModalWindow from "../../../components/ModalWindow/ModalWindow";
import { ContextProvider } from "../../../contextProvider";
import {
  AppreciationCertificate,
  EducationCertificatesResponse,
  FederationsCertificatesResponse,
  QualificationCertificatesResponse,
  SpecialistData,
} from "../../../types/TypeSpecialist";
import ApppreciationCard from "./ApppreciationCard/ApppreciationCard";
import EducationCard from "./EducationCard/EducationCard";
import s from "./EducationSection.module.css";
import FederationCard from "./FederationCard/FederationCard";
import QualificationCard from "./QualificationCard/QualificationCard";

type EducationSectionProps = {
  specialistId: string;
  userId: string;
  isPublicProfile: boolean;
  specialistData: SpecialistData;
};

export type CertificatesData = {
  education: EducationCertificatesResponse[] | undefined;
  qualification: QualificationCertificatesResponse[] | undefined;
  federation: FederationsCertificatesResponse[] | undefined;
  apppreciation: AppreciationCertificate[] | undefined;
};

const EducationSection = ({
  specialistId,
  userId,
  isPublicProfile,
  specialistData,
}: EducationSectionProps) => {
  const token = localStorage.getItem("token");
  const { userData } = useContext(ContextProvider);
  const [certificatesData, setCertificatesData] = useState<CertificatesData>({
    education: undefined,
    qualification: undefined,
    federation: undefined,
    apppreciation: undefined,
  });

  const [previewFile, setPreviewFile] = useState<{
    pdfFile: string | null;
    imageFile: string | null;
  }>({ pdfFile: null, imageFile: null });
  const [previewFileModal, setPreviewFileModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  const getEducationCertificates = async () => {
    if (!token) return;
    const response = await SpecialistApi.getEducationCertificates(
      token,
      specialistId,
      1,
      50,
    );

    return { total: response.total, certificates: response.certificates };
  };
  const getEducationPublicCertificates = async () => {
    const response = await SpecialistApi.getEducationPublicCertificates(
      specialistId,
      1,
      50,
    );

    return { total: response.total, certificates: response.certificates };
  };
  const getFederationCertificates = async () => {
    if (!token) return;
    const response = await SpecialistApi.getFederationCertificates(
      token,
      specialistId,
      1,
      50,
    );
    return { total: response.total, certificates: response.certificates };
  };
  const getFederationPublicCertificates = async () => {
    const response = await SpecialistApi.getFederationPublicCertificates(
      specialistId,
      1,
      50,
    );
    return { total: response.total, certificates: response.certificates };
  };
  const getQualificationCertificates = async () => {
    if (!token) return;
    const response = await SpecialistApi.getQualificationCertificates(
      token,
      specialistId,
      1,
      50,
    );
    return { total: response.total, certificates: response.certificates };
  };
  const getQualificationPublicCertificates = async () => {
    const response = await SpecialistApi.getQualificationPublicCertificates(
      specialistId,
      1,
      50,
    );
    return { total: response.total, certificates: response.certificates };
  };

  const getAppreciationCertificates = async () => {
    if (!token) return;
    const response = await SpecialistApi.getAppreciationCertificates(
      token,
      specialistId,
      1,
      100,
    );

    return { total: response.total, certificates: response.certificates };
  };
  const getAppreciationPublicCertificates = async () => {
    const response = await SpecialistApi.getAppreciationPublicCertificates(
      specialistId,
      1,
      100,
    );

    return { total: response.total, certificates: response.certificates };
  };

  useEffect(() => {
    const makeAsync = async () => {
      setIsLoading(true);

      try {
        const getEducation = !isPublicProfile
          ? getEducationCertificates
          : getEducationPublicCertificates;

        const getFederation = !isPublicProfile
          ? getFederationCertificates
          : getFederationPublicCertificates;

        const getQualification = !isPublicProfile
          ? getQualificationCertificates
          : getQualificationPublicCertificates;
        const getAppreciation = !isPublicProfile
          ? getAppreciationCertificates
          : getAppreciationPublicCertificates;
        const [
          educationData,
          federationData,
          qualificationData,
          appreciationData,
        ] = await Promise.all([
          getEducation(),
          getFederation(),
          getQualification(),
          getAppreciation(),
        ]);
        setCertificatesData({
          education: educationData?.certificates,
          federation: federationData?.certificates,
          qualification: qualificationData?.certificates,
          apppreciation: appreciationData?.certificates,
        });
      } catch (error) {
        console.error("Error fetching certificates data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    makeAsync();
  }, [specialistId, userId]);

  useEffect(() => {
    setPreviewFile((prev) => ({ ...prev, imageFile: null }));
  }, []);
  const previewFileHandler = (link: string, type: string) => {
    if (type.toLowerCase().includes("pdf")) {
      setPreviewFile({
        imageFile: null,
        pdfFile: link,
      });
    } else {
      setPreviewFile({
        imageFile: link,
        pdfFile: null,
      });
    }
    setPreviewFileModal(true);
  };

  if (isLoading) {
    return (
      <div className={s.loaderContainer}>
        <Loader size={100} />
      </div>
    );
  }
  if (
    !certificatesData.education &&
    !certificatesData.federation &&
    !certificatesData.qualification
  ) {
    return null;
  }
  return (
    <div className={s.container}>
      {certificatesData.education && certificatesData.education.length > 0 && (
        <div className={`${s.educationBlock} ${s.borderBottomBlock}`}>
          <span className={s.certificateTypeTitle}>Education</span>
          <div className={s.certificateListBlock}>
            {certificatesData.education.map((item, index) => (
              <EducationCard
                educationData={item}
                previewFileHandler={previewFileHandler}
                specialistId={specialistId}
                specialistData={specialistData}
                setCertificatesData={setCertificatesData}
                key={index}
              />
            ))}
          </div>
        </div>
      )}
      {certificatesData.federation &&
        certificatesData.federation.length > 0 && (
          <div className={`${s.educationBlock} ${s.borderBottomBlock}`}>
            <span className={s.certificateTypeTitle}>Federations</span>
            <div className={s.certificateListBlock}>
              {certificatesData.federation.map((item, index) => (
                <FederationCard
                  federationData={item}
                  previewFileHandler={previewFileHandler}
                  specialistId={specialistId}
                  specialistData={specialistData}
                  setCertificatesData={setCertificatesData}
                  key={index}
                />
              ))}
            </div>
          </div>
        )}
      {certificatesData.qualification &&
        certificatesData.qualification.length > 0 && (
          <div className={s.educationBlock}>
            <span className={s.certificateTypeTitle}>Qualifications</span>
            <div className={s.certificateListBlock}>
              {certificatesData.qualification.map((item, index) => (
                <QualificationCard
                  qualificationData={item}
                  previewFileHandler={previewFileHandler}
                  specialistId={specialistId}
                  specialistData={specialistData}
                  setCertificatesData={setCertificatesData}
                  key={index}
                />
              ))}
            </div>
          </div>
        )}
      {certificatesData.apppreciation &&
        certificatesData.apppreciation.length > 0 && (
          <div className={s.educationBlock}>
            <span className={s.certificateTypeTitle}>Apppreciation</span>
            <div className={s.certificateListBlock}>
              {certificatesData.apppreciation.map((item, index) => (
                <ApppreciationCard
                  appreciationData={item}
                  previewFileHandler={previewFileHandler}
                  specialistId={specialistId}
                  specialistData={specialistData}
                  setCertificatesData={setCertificatesData}
                  key={index}
                />
              ))}
            </div>
          </div>
        )}
      <ModalWindow
        isOpen={previewFileModal}
        setIsOpen={setPreviewFileModal}
        bgColor="transparent"
        height="fit-content"
        width={"fit-content"}
        padding="0"
        maxWidth="fit-content"
      >
        <div
          className={
            previewFile.pdfFile ? s.pdfPreviewBlock : s.imagePreviewBlock
          }
        >
          {previewFile.pdfFile && (
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.js">
              <div className={s.pdfWorker}>
                <Viewer
                  fileUrl={previewFile.pdfFile}
                  plugins={[defaultLayoutPluginInstance]}
                />
              </div>
            </Worker>
          )}

          {previewFile.imageFile && (
            <img src={previewFile.imageFile} alt="" className={s.previewImg} />
          )}
        </div>
      </ModalWindow>
    </div>
  );
};

export default EducationSection;
